/**
 * Product Hub — ליקוט הזמנות. The warehouse floor's vocabulary and its five pure decisions.
 *
 * The orders module built the **manager's** screen: every order from every store, three statuses,
 * exceptions, automatic warehouse assignment. It stops exactly where the physical work starts —
 * `fulfillmentStatus = 'ready'` means "a warehouse was assigned and it can be picked", and until now
 * nothing let anybody pick it. This file is the other side of that line.
 *
 * It is deliberately **not** a subset of `productHub.orders.ts` (אפיון §1). A picker answers four
 * questions — which order, which products, how many units, and did it finish or is something wrong —
 * and the shapes here carry that and nothing else. §7 is a hard rule rather than a styling note:
 * there is no price, no total, no payment status, no email and no street address in `PickTask`,
 * **including in the JSON**, because the only way to guarantee a screen never shows something is for
 * the server never to send it.
 *
 * ## Why picking has no statuses of its own in the database
 *
 * `PICK_STATUSES` looks like a new enum and is not one: it is a *view* of
 * `ORDER_FULFILLMENT_STATUSES`, collapsed to the four states a warehouse worker distinguishes.
 * Storing it would mean two columns that must agree about the same order, and the first time they
 * disagreed the picker and the manager would be looking at different orders with the same number.
 *
 * No React, no MUI, no browser globals — the backend imports this file directly through
 * `@malash-office/types/product-hub/picking`.
 */
import type { ShippingAttribute } from './productHub.fulfillment';
import { PhOption, phOptions } from './productHub.core';
import type { OrderFulfillmentStatus, OrderIssueType } from './productHub.orders';

/* ------------------------------------------------------------------ */
/* Status — what the picker sees                                       */
/* ------------------------------------------------------------------ */

/** The four states of אפיון §8. A projection of `fulfillmentStatus`, never a stored column. */
export const PICK_STATUSES = ['pending', 'picking', 'picked', 'issue'] as const;
export type PickStatus = (typeof PICK_STATUSES)[number];

/**
 * One label per status, used by the filter, the card chip and the row alike.
 *
 * §8 words the filter option "ממתין לליקוט" while §6's own example prints "מוכן לליקוט" on the
 * card. They are the same state, so it gets one label — the mockup's — rather than a filter whose
 * wording does not match the chip it filters for.
 */
export const PICK_STATUS_LABELS: Record<PickStatus, string> = {
  pending: 'מוכן לליקוט',
  picking: 'בליקוט',
  picked: 'לוקט',
  issue: 'דורש טיפול'
};

export const PICK_STATUS_OPTIONS: PhOption<PickStatus>[] = phOptions(PICK_STATUSES, PICK_STATUS_LABELS);

/** The four tiles of §5. Plural, because a tile counts orders while a chip labels one. */
export const PICK_COUNTER_LABELS: Record<PickStatus, string> = {
  pending: 'ממתינות לליקוט',
  picking: 'בליקוט עכשיו',
  picked: 'לוקטו',
  issue: 'דורשות טיפול'
};

/**
 * The fulfillment statuses that are warehouse work at all (§35).
 *
 * `unassigned` is missing because there is nothing to pick until a warehouse is chosen, and
 * `shipped`/`completed` because the goods have left. The list is also the server-side filter: an
 * order outside it never becomes a pick task, whatever its issues say.
 */
export const PICKABLE_FULFILLMENT_STATUSES: readonly OrderFulfillmentStatus[] = [
  'ready',
  'picking',
  'picked',
  'packed',
  'ready_pickup',
  'issue'
];

/**
 * What the picker's four states mean in terms of the order's own column.
 *
 * Open issues are deliberately **not** a second input. A reported shortage does not move the order
 * out of `picking` — the picker is still holding it, and §29 hands it over only at "סיום ליקוט",
 * which is where `fulfillmentStatus` becomes `issue`. Reading the issues here as well would let the
 * tile and the card disagree about the same order.
 */
export const pickStatusOf = (fulfillmentStatus: OrderFulfillmentStatus): PickStatus => {
  if (fulfillmentStatus === 'picking') return 'picking';
  if (fulfillmentStatus === 'issue') return 'issue';
  if (fulfillmentStatus === 'ready' || fulfillmentStatus === 'unassigned') return 'pending';
  return 'picked';
};

/* ------------------------------------------------------------------ */
/* Delivery                                                            */
/* ------------------------------------------------------------------ */

/**
 * How the goods leave, in the only two forms an order can record (§8, §25).
 *
 * The mockup's filter offers a third, "שילוח ספק". It has no representation here on purpose: an
 * order carries a shipping *method* snapshot, and whether a supplier ships instead of us is a
 * property of the product's fulfillment settings, not of the order. An order a supplier ships is
 * not a warehouse task in the first place, so the value would only ever select nothing.
 */
export const PICK_DELIVERY_TYPES = ['ship', 'pickup'] as const;
export type PickDeliveryType = (typeof PICK_DELIVERY_TYPES)[number];

export const PICK_DELIVERY_TYPE_LABELS: Record<PickDeliveryType, string> = {
  ship: 'משלוח',
  pickup: 'איסוף עצמי'
};

export const PICK_DELIVERY_TYPE_OPTIONS: PhOption<PickDeliveryType>[] = phOptions(
  PICK_DELIVERY_TYPES,
  PICK_DELIVERY_TYPE_LABELS
);

/** `shippingMethodType` is a snapshot on the order, so this reads the order and never the method. */
export const pickDeliveryTypeOf = (shippingMethodType: string | null): PickDeliveryType =>
  shippingMethodType === 'pickup' ? 'pickup' : 'ship';

/* ------------------------------------------------------------------ */
/* Issues the picker reports (אפיון §21–§23, §36)                       */
/* ------------------------------------------------------------------ */

/**
 * The six options of the "יש בעיה" sheet, in the order §21 lists them.
 *
 * Five of them are new codes on `OrderIssue`. The sixth, `insufficient`, is not: a shortage is
 * already `no_stock`/`partial_stock`, which intake raises and the manager's screen already filters
 * and counts. Giving the picker a seventh code for the same fact would mean the "בעיית מלאי"
 * tile counted one of them and not the other.
 *
 * "מיקום שגוי" is absent because §21 forbids it — there is no location management in the warehouse
 * to be wrong about.
 */
export const PICK_ISSUE_TYPES = [
  'not_found',
  'insufficient',
  'damaged',
  'barcode_mismatch',
  'wrong_item',
  'other'
] as const;
export type PickIssueType = (typeof PICK_ISSUE_TYPES)[number];

export const PICK_ISSUE_TYPE_LABELS: Record<PickIssueType, string> = {
  not_found: 'המוצר לא נמצא',
  insufficient: 'אין מספיק מלאי',
  damaged: 'מוצר פגום',
  barcode_mismatch: 'ברקוד לא תואם',
  wrong_item: 'מוצר שגוי',
  other: 'בעיה אחרת'
};

export const PICK_ISSUE_TYPE_OPTIONS: PhOption<PickIssueType>[] = phOptions(PICK_ISSUE_TYPES, PICK_ISSUE_TYPE_LABELS);

/**
 * Which of them ask for a number, and what to call the field (§22, §23).
 *
 * "המוצר לא נמצא" does not ask: none of it was there, so the number is already known. The two that
 * do ask mean different things by it — §22 wants how many *were* found, §23 how many are damaged —
 * which is why the label is here rather than a single "כמות" in the sheet.
 */
export const PICK_ISSUE_QUANTITY_LABELS: Partial<Record<PickIssueType, string>> = {
  insufficient: 'כמה נמצאו בפועל?',
  damaged: 'כמות פגומה'
};

export const pickIssueNeedsQuantity = (type: PickIssueType): boolean => type in PICK_ISSUE_QUANTITY_LABELS;

/**
 * The `OrderIssue.type` a reported problem is filed under.
 *
 * Only `insufficient` branches, and it branches on the same question intake asks: nothing on the
 * shelf is `no_stock`, some of it is `partial_stock`. The rest map one to one, which is what makes a
 * picker's report readable in the manager's alert without a second vocabulary.
 *
 * `other` becomes `pick_other` rather than keeping its name: `OrderIssueType` is a single namespace
 * shared with intake and reconciliation, and a bare "other" there would say nothing about where the
 * report came from.
 */
const ORDER_ISSUE_TYPE_BY_PICK_ISSUE: Record<Exclude<PickIssueType, 'insufficient'>, OrderIssueType> = {
  not_found: 'not_found',
  damaged: 'damaged',
  barcode_mismatch: 'barcode_mismatch',
  wrong_item: 'wrong_item',
  other: 'pick_other'
};

export const orderIssueTypeOfPickIssue = (type: PickIssueType, foundQuantity: number): OrderIssueType =>
  type === 'insufficient' ? (foundQuantity > 0 ? 'partial_stock' : 'no_stock') : ORDER_ISSUE_TYPE_BY_PICK_ISSUE[type];

/* ------------------------------------------------------------------ */
/* Progress — one fold, read by four places (אפיון §26)                 */
/* ------------------------------------------------------------------ */

/** The minimum a line has to expose for the progress and the finish rule to read it. */
export interface PickProgressItem {
  /** What the customer ordered. Never changes — see ADR-020 §1. */
  quantity: number;
  /** What was physically collected. */
  pickedQuantity: number;
  /** An **open** issue was reported on this line. A resolved one is history, not a blocker. */
  hasIssue: boolean;
}

export interface PickProgress {
  /** Units collected. */
  units: number;
  /** Units ordered. */
  requiredUnits: number;
  lines: number;
  /** Lines collected in full — an unfinished line with an issue does not count as done. */
  doneLines: number;
  /**
   * Units nobody could collect: the shortfall on lines that reported a problem.
   *
   * Only issue lines contribute. A line at 2 of 3 that the picker has simply not finished yet is
   * not "missing" — it is in progress, and calling it missing would put the order in the manager's
   * queue while it is still being worked on.
   */
  missingUnits: number;
  /** 0–100, rounded. Zero required units gives 0 rather than NaN. */
  pct: number;
}

/**
 * The single fold behind §26, the sticky bottom bar, the card progress and the success screen.
 *
 * They all say a version of "N מתוך M", and computing it four times is how three of them end up
 * disagreeing after the fifth change to the rules.
 */
export const pickProgress = (items: readonly PickProgressItem[]): PickProgress => {
  const requiredUnits = items.reduce((sum, item) => sum + item.quantity, 0);
  const units = items.reduce((sum, item) => sum + Math.min(item.pickedQuantity, item.quantity), 0);

  return {
    units,
    requiredUnits,
    lines: items.length,
    doneLines: items.filter(item => item.pickedQuantity >= item.quantity).length,
    missingUnits: items.reduce(
      (sum, item) => sum + (item.hasIssue ? Math.max(0, item.quantity - item.pickedQuantity) : 0),
      0
    ),
    pct: requiredUnits ? Math.round((units / requiredUnits) * 100) : 0
  };
};

/** A line nobody has to come back to: either it is complete, or somebody said why it never will be. */
export const isPickLineResolved = (item: PickProgressItem): boolean =>
  item.pickedQuantity >= item.quantity || item.hasIssue;

/**
 * §28 — the rule that stops a silent finish.
 *
 * "סיום ליקוט" is enabled only when every line is either picked in full or has a reported issue.
 * The case it exists for is the line the picker never touched: without this, walking away from a
 * product would close the order as if it had been packed, and the customer would find out.
 *
 * An order with no lines cannot be finished either. It should not exist, and answering "yes, done"
 * about nothing is the wrong way to discover that it does.
 */
export const canFinishPicking = (items: readonly PickProgressItem[]): boolean =>
  items.length > 0 && items.every(isPickLineResolved);

/**
 * The fulfillment status "סיום ליקוט" lands on.
 *
 * Missing units hand the order to a manager (§29); a clean pick moves it to `picked`. Kept here
 * beside the finish rule so the two cannot be changed apart.
 */
export const fulfillmentStatusAfterPicking = (progress: PickProgress): OrderFulfillmentStatus =>
  progress.missingUnits > 0 ? 'issue' : 'picked';

/** 0 ≤ picked ≤ ordered, in one place. §15: "אין להגדיל מעבר לכמות ההזמנה". */
export const clampPicked = (required: number, next: number): number => Math.max(0, Math.min(required, next));

/* ------------------------------------------------------------------ */
/* Scanning (אפיון §15, §39)                                            */
/* ------------------------------------------------------------------ */

/** The three answers a scan can have. */
export const PICK_SCAN_KINDS = ['matched', 'already_full', 'not_in_order'] as const;
export type PickScanKind = (typeof PICK_SCAN_KINDS)[number];

export interface PickScanItem extends PickProgressItem {
  id: number;
  sku: string | null;
  barcode: string | null;
}

export interface PickScanTarget<T extends PickScanItem = PickScanItem> {
  kind: PickScanKind;
  /** The line the code identified. Absent only for `not_in_order`. */
  item?: T;
}

/**
 * Which line a scanned code refers to, and whether it can still take a unit.
 *
 * A pure function so the three outcomes of §15 can be tested without a camera and without a DOM —
 * the scanner itself is the one part of this feature that cannot be verified from a development
 * machine, and none of the logic should be trapped behind it.
 *
 * Barcode first, then SKU: a warehouse scanner reads the barcode, but the same field also accepts a
 * typed SKU, and a SKU that happens to equal another product's barcode should resolve as the
 * barcode it physically is. SKU comparison is case-insensitive because people type it.
 *
 * When two lines carry the same SKU, the one still short of its quantity wins. `find` would keep
 * returning the first line and report "already picked" while the second still needed units.
 */
export const nextScanTarget = <T extends PickScanItem>(items: readonly T[], code: string): PickScanTarget<T> => {
  const needle = code.trim();
  if (!needle) return { kind: 'not_in_order' };

  const lower = needle.toLowerCase();
  const matches = items.filter(item => item.barcode === needle || item.sku?.toLowerCase() === lower);
  if (!matches.length) return { kind: 'not_in_order' };

  const open = matches.find(item => item.pickedQuantity < item.quantity);
  return open ? { kind: 'matched', item: open } : { kind: 'already_full', item: matches[0] };
};

/** The Hebrew §39 puts on each outcome. The scanned code is appended by the caller that has it. */
export const PICK_SCAN_MESSAGES: Record<PickScanKind, string> = {
  matched: 'מוצר זוהה',
  already_full: 'כל הכמות הנדרשת ממוצר זה כבר לוקטה',
  not_in_order: 'המוצר שנסרק אינו שייך להזמנה'
};

/* ------------------------------------------------------------------ */
/* Row shapes — §7 is enforced by their absence                        */
/* ------------------------------------------------------------------ */

/** A problem reported on one line, as the screen reads it back. */
export interface PickItemIssue {
  id: number;
  /** The stored `OrderIssue.type`, so the manager's screen and this one name it the same way. */
  type: OrderIssueType;
  /** Hebrew, written by the server: "חסרות 2 יחידות · SKU SID-BLU-ARI". */
  detail: string;
  /** How many are missing or damaged. Null for the codes §22/§23 do not ask a number for. */
  quantity: number | null;
}

/**
 * One product to collect.
 *
 * `barcode` is the live variant's, not a snapshot: it is used to identify the thing in the picker's
 * hand right now, which is the one case where the current catalogue is the correct source. Null on
 * an unmatched line, which is also why scanning cannot be the only way to pick.
 */
export interface PickTaskItem {
  id: number;
  productName: string;
  variantLabel: string | null;
  sku: string | null;
  barcode: string | null;
  imageUrl: string | null;
  quantity: number;
  pickedQuantity: number;
  /** §24 — only the flags that change how it is handled, never the customer's note. */
  flags: ShippingAttribute[];
  /** §24's "יש לארוז בנפרד" needs the words the product's settings carry. */
  specialHandlingNote: string | null;
  issue: PickItemIssue | null;
}

/**
 * One row of the task list (§6).
 *
 * **What is missing is the specification.** No email, no address, no unit price, no order total, no
 * discount and no payment status — §7 forbids all of them, and a serializer that omits them is the
 * only way to be sure a future card cannot render them by accident. There is a test for exactly
 * this in the backend.
 *
 * `customerPhone` is here because §6 asks for it: the warehouse calls people about their packages.
 */
export interface PickTask {
  /** The order id. The URL and every write use it; `orderNumber` is only ever displayed. */
  id: number;
  orderNumber: string;
  storeName: string;
  customerName: string;
  customerPhone: string | null;
  /** ISO. §6 shows the time it arrived — "התקבלה 12:43". */
  receivedAt: string;
  linesCount: number;
  unitsCount: number;
  pickedUnits: number;
  missingUnits: number;
  deliveryType: PickDeliveryType;
  /** The method's snapshotted name — "משלוח עד הבית". Falls back to the type's label. */
  deliveryLabel: string;
  status: PickStatus;
  warehouseId: number | null;
  pickedByUserId: number | null;
  /** "בליקוט על ידי דוד כהן" (§10, §32). Null when nobody holds it. */
  pickedByName: string | null;
}

/**
 * What the pack step reads (the mockup's `pkIsShip`).
 *
 * A deliberately short address: recipient, city, phone. §25 keeps the full address off the picking
 * screen, and the pack screen does not call a carrier or print a document — it records that the
 * boxes are closed. A street and a postcode would be information nobody on this screen acts on.
 *
 * There is no weight: no order or product column holds one, and inventing an empty row for the
 * mockup's "משקל" would be a field that is always "—".
 */
export interface PickShipSummary {
  recipientName: string | null;
  city: string | null;
  phone: string | null;
  methodName: string | null;
  providerName: string | null;
  pickupPointName: string | null;
}

/** The work screen: a task plus its lines and the little the pack step adds. */
export interface PickTaskDetail extends PickTask {
  items: PickTaskItem[];
  ship: PickShipSummary;
  packagesCount: number | null;
  trackingNumber: string | null;
  pickingStartedAt: string | null;
  pickedAt: string | null;
  packedAt: string | null;
}

/** §5's four tiles, counted over the picker's warehouse rather than over the current filter. */
export type PickCounters = Record<PickStatus, number>;

/** The list endpoint answers both in one response — the tiles are never a second round trip. */
export interface PickTaskListResult {
  tasks: PickTask[];
  counters: PickCounters;
}

/**
 * A warehouse the queue can be narrowed to (§4). One of them means no selector at all.
 *
 * Every live warehouse, not the signed-in user's subset: `orders:pick` is the whole gate, and an
 * order in `ready` is in the queue whoever is looking. The list is a filter, never a permission.
 */
export interface PickWarehouse {
  id: number;
  name: string;
}

/** List params, all strings — they come from the URL and go back to it, like the orders screen. */
export interface PickTaskListParams {
  /** Narrows the queue to one warehouse. Absent means all of them, which is the default view. */
  warehouseId?: string;
  /** Comma-separated `PickStatus`. */
  status?: string;
  /** Comma-separated `PickDeliveryType`. */
  delivery?: string;
  /** `'true'` narrows to the orders §36 calls the picker's own. */
  attention?: string;
  /** Order number, SKU or customer name (§9). */
  searchTerm?: string;
}

/* ------------------------------------------------------------------ */
/* Write bodies                                                        */
/* ------------------------------------------------------------------ */

/**
 * None of these carry a `version`.
 *
 * The lock on a pick task is **ownership**, not optimism: `pickedByUserId` is claimed at "התחל
 * ליקוט" and every later write checks it, so two pickers on one order is already impossible. A
 * version on top of that would only ever fire on one person's own fast `+` presses — a 409 that
 * looks exactly like a bug and is not one.
 */
export interface PickItemQuantityBody {
  pickedQuantity: number;
}

export interface PickScanBody {
  code: string;
}

export interface PickIssueBody {
  type: PickIssueType;
  /** Required for the two types of `PICK_ISSUE_QUANTITY_LABELS`, ignored for the rest. */
  quantity?: number;
  note?: string | null;
}

export interface PickPackBody {
  packagesCount: number;
  /** Typed in by hand — nothing here calls a carrier. Null for self-collection. */
  trackingNumber?: string | null;
}

/** The answer to a scan: the outcome, the message, and the line as it now stands. */
export interface PickScanResult {
  kind: PickScanKind;
  message: string;
  /** The code as scanned, so §39 can print it back on a miss. */
  code: string;
  item: PickTaskItem | null;
  progress: PickProgress;
}
